(function (window, document) {
  'use strict';

  var FORM_KEY = 'mnb:new-listing:v1:form';
  var MAX_PHOTOS = 10;
  // FormAccordion.tsx:400-406 — SubmissionDialog's action is the tracked
  // conversion destination for a successful listing.
  var THANK_YOU_URL = 'https://mynextbike.co.uk/pages/thank-you?cmc_goal=a&cmc_ref=sign-up&cmc_redir=https%3A%2F%2Fmynextbike.co.uk%2Fpages%2Fthank-you';
  var MODELS_KEY = 'mnb:new-listing:v1:models';
  // The assisted-identification flow is simply how listing works now: it ran as
  // a test, it won, and there is no arm, bucket or kill switch left to reason
  // about — so there is no longer any variant to stamp on events or payloads.
  // Whether a given seller entered the assisted path is a per-seller fact that
  // still travels as `assistedIdentification`.
  // The suggested-price wording test ran and the speed-of-sale framing beat the
  // current grade copy, so the winning wording shipped as the widget's only
  // wording: no arm, bucket, kill switch or `copy-variant` left to reason about.
  var THREE_DAYS = 3 * 24 * 60 * 60 * 1000;
  var modules = Object.create(null);
  var bootedStore = null;
  var bootedRoot = null;
  var bootedConfig = {};
  var openedSections = Object.create(null);
  // AccordionSection.tsx:31-41 (hasBeenOpened): a section only surfaces its
  // Complete/Incomplete badge once the seller has actually opened it, and
  // keeps surfacing it after it is collapsed again.
  var revealedSections = Object.create(null);
  // TextInput.tsx and its siblings never render a field-level error message at
  // all in the source app — only the section badge exists there. This port
  // adds inline "This field is required" text for accessibility, but it must
  // not fire the instant a section opens: a field only shows its message once
  // the seller has actually left that field empty, or after a submit attempt.
  var touchedFields = Object.create(null);
  var completedSections = Object.create(null);
  var funnelSteps = Object.create(null);
  var submitting = false;
  // Bike Valuation has the highest dropoff of any section, so each of its
  // inputs is tracked individually (not just the section as a whole) to see
  // exactly where sellers abandon the flow.
  var bikeValuationFieldOrder = ['Brand', 'Model', 'BikeYear', 'bike_data_Rrp', 'condition', 'ListingPrice'];
  var assistedBikeValuationFieldOrder = [
    'BikeType',
    'bike_identification_frame_text',
    'bike_identification_age_range',
    'bike_identification_price_band',
    'condition',
    'ListingPrice'
  ];
  var bikeValuationFieldsCompleted = Object.create(null);
  var contactDetailsFieldOrder = [
    'user_NameFirst',
    'user_NameLast',
    'user_Email',
    'user_PhoneNumber',
    'address_house_number',
    'address_street_name',
    'address_postcode',
    'address_countryCode'
  ];
  var contactDetailsFieldsCompleted = Object.create(null);

  // Klaviyo injects its bottom-right "Default Flyout" signup on every page
  // (delay/scroll/exit-intent triggers, no URL exclusions in its targeting),
  // so it lands on this flow too. The theme's popup section is unrelated and
  // already off here; this detached signup must be removed instead of merely
  // hidden, because Klaviyo's fixed-position wrapper survives a CSS hide and
  // would still intercept clicks at the dock position. new-listing.css hides
  // the rendered nodes as a first line of defence; this observer removes the
  // whole container so nothing Klaviyo-owned is left in the page.
  function suppressKlaviyoFlyout() {
    if (!window.MutationObserver || !document.body) return;

    function isKlaviyoFlyout(node) {
      if (!node || node.nodeType !== 1) return false;
      if (node.matches('.klaviyo-form, .klaviyo-close-form, [class*="klaviyo-teaser"]')) return true;
      return !!node.querySelector('.klaviyo-form, .klaviyo-close-form, [class*="klaviyo-teaser"]');
    }

    function removeFlyoutRoot(node) {
      var root = node;
      while (root.parentElement && root.parentElement !== document.body) {
        root = root.parentElement;
      }
      if (root.parentElement) root.parentElement.removeChild(root);
    }

    function sweepExisting() {
      var nodes = document.querySelectorAll('.klaviyo-form, .klaviyo-close-form, [class*="klaviyo-teaser"]');
      for (var i = 0; i < nodes.length; i++) removeFlyoutRoot(nodes[i]);
    }

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (isKlaviyoFlyout(added[j])) removeFlyoutRoot(added[j]);
        }
      }
    });

    sweepExisting();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function defineTooltip() {
    if (!window.customElements || window.customElements.get('nl-tooltip')) return;

    function NLTooltip() {
      return Reflect.construct(HTMLElement, [], NLTooltip);
    }

    NLTooltip.prototype = Object.create(HTMLElement.prototype);
    NLTooltip.prototype.constructor = NLTooltip;

    NLTooltip.prototype.connectedCallback = function () {
      if (this._connected) return;
      this._connected = true;
      this._trigger = this.querySelector('[data-tooltip-trigger]');
      this._content = this.querySelector('[data-tooltip-content]');
      if (!this._trigger || !this._content) return;

      if (!this._content.id) this._content.id = 'nl-tooltip-' + Math.random().toString(36).slice(2, 10);
      this._trigger.setAttribute('aria-describedby', this._content.id);
      this.hide();

      var self = this;
      this._onPointerEnter = function () { self.show(); };
      this._onPointerLeave = function () { self.hide(); };
      this._onFocusIn = function () { self.show(); };
      this._onFocusOut = function () {
        window.setTimeout(function () {
          if (!self.contains(document.activeElement)) self.hide();
        }, 0);
      };
      this._onClick = function () {
        if (self._content.hidden) self.show();
        else self.hide();
      };
      this._onKeyDown = function (event) {
        if (event.key !== 'Escape' || self._content.hidden) return;
        self.hide();
        self._trigger.focus();
      };
      this._onDocumentPointerDown = function (event) {
        if (!self.contains(event.target)) self.hide();
      };

      this.addEventListener('pointerenter', this._onPointerEnter);
      this.addEventListener('pointerleave', this._onPointerLeave);
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);
      this._trigger.addEventListener('click', this._onClick);
      document.addEventListener('keydown', this._onKeyDown);
      document.addEventListener('pointerdown', this._onDocumentPointerDown);
    };

    NLTooltip.prototype.disconnectedCallback = function () {
      if (!this._connected) return;
      this.restoreContent();
      this._connected = false;
      this.removeEventListener('pointerenter', this._onPointerEnter);
      this.removeEventListener('pointerleave', this._onPointerLeave);
      this.removeEventListener('focusin', this._onFocusIn);
      this.removeEventListener('focusout', this._onFocusOut);
      if (this._trigger) this._trigger.removeEventListener('click', this._onClick);
      document.removeEventListener('keydown', this._onKeyDown);
      document.removeEventListener('pointerdown', this._onDocumentPointerDown);
    };

    NLTooltip.prototype.show = function () {
      if (!this._trigger || !this._content) return;
      this.portalContent();
      this._content.hidden = false;
      this.positionContent();
      this._trigger.setAttribute('aria-expanded', 'true');
    };

    NLTooltip.prototype.hide = function () {
      if (!this._trigger || !this._content) return;
      this._content.hidden = true;
      this._trigger.setAttribute('aria-expanded', 'false');
      this.restoreContent();
    };

    NLTooltip.prototype.portalContent = function () {
      if (this._content.parentNode === document.body) return;
      this._contentAnchor = document.createComment('nl-tooltip-content');
      this._content.parentNode.insertBefore(this._contentAnchor, this._content);
      this._content.style.setProperty('--nl-tooltip-divider', getComputedStyle(this).getPropertyValue('--divider'));
      this._content.style.setProperty('--nl-tooltip-card', getComputedStyle(this).getPropertyValue('--card'));
      this._content.style.setProperty('--nl-tooltip-muted', getComputedStyle(this).getPropertyValue('--fog-600'));
      this._content.style.setProperty('--nl-tooltip-radius', getComputedStyle(this).getPropertyValue('--radius-sm'));
      this._content.style.width = getComputedStyle(this._content).width;
      this._content.setAttribute('data-nl-tooltip-portal', '');
      document.body.appendChild(this._content);
    };

    NLTooltip.prototype.positionContent = function () {
      var triggerRect = this._trigger.getBoundingClientRect();
      var contentWidth = this._content.offsetWidth || 280;
      var contentHeight = this._content.offsetHeight || 0;
      var viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      var gutter = 16;
      var left = Math.max(gutter, Math.min(triggerRect.left, viewportWidth - contentWidth - gutter));
      var top = triggerRect.bottom + 6;

      if (top + contentHeight > viewportHeight - gutter && triggerRect.top - contentHeight - 6 >= gutter) {
        top = triggerRect.top - contentHeight - 6;
      }

      this._content.style.left = Math.round(left) + 'px';
      this._content.style.top = Math.round(top) + 'px';
    };

    NLTooltip.prototype.restoreContent = function () {
      if (!this._content || this._content.parentNode !== document.body) return;
      this._contentAnchor.parentNode.insertBefore(this._content, this._contentAnchor);
      this._contentAnchor.remove();
      this._contentAnchor = null;
      this._content.removeAttribute('data-nl-tooltip-portal');
      ['--nl-tooltip-divider', '--nl-tooltip-card', '--nl-tooltip-muted', '--nl-tooltip-radius', 'width', 'left', 'top'].forEach(function (property) {
        this._content.style.removeProperty(property);
      }, this);
    };

    window.customElements.define('nl-tooltip', NLTooltip);
  }

  defineTooltip();

  var sectionEvents = {
    'bike-valuation': 'identify-bike',
    'bike-specifications': 'bike-specifications',
    photos: 'images',
    'additional-details': 'admin-details',
    'contact-information': 'contact-details'
  };

  var funnelOrder = [
    'bike-valuation',
    'bike-specifications',
    'photos',
    'additional-details'
  ];

  var blankForm = {
    internal_enquiry_type: '',
    internal_listWithoutTemplate: false,
    internal_assisted_identification: false,
    internal_assisted_identification_entry_point: '',
    internal_year_not_sure: false,
    bike_identification_frame_text: '',
    bike_identification_family: '',
    bike_identification_age_range: '',
    bike_identification_price_band: '',
    internal_media_drive: [],
    internal_media_drive_preview: [],
    internal_media_additional: [],
    internal_media_additional_preview: [],
    internal_media_side: [],
    internal_media_side_preview: [],
    internal_media_drive_close: [],
    internal_media_drive_close_preview: [],
    internal_phone_number_verified: false,
    internal_user_id_verified: 'unverified',
    internal_processing_files: false,
    internal_electric_bike: '',
    internal_bike_has_size_guide: false,
    media_drive: [],
    media_additional: [],
    media_side: [],
    media_drive_close: [],
    media_video: [],
    media_proof_of_purchase: [],
    address_house_number: '',
    address_street_name: '',
    address_city: '',
    address_state: '',
    address_postcode: '',
    address_country: 'United Kingdom',
    address_countryCode: 'GB',
    condition: '',
    BikeColour: '',
    ChangedPartsDescription: '',
    UpgradesReplacements: '',
    BikeShortDescription: '',
    ListingPrice: '',
    user_ReferralSource: '',
    user_PhoneNumber: '',
    user_Email: '',
    user_NameLast: '',
    user_NameFirst: '',
    user_id: '',
    EbikeWeight: '',
    EbikeMaxCharge: '',
    EbikePowerOutput: '',
    Height: '',
    EbikeMileage: '',
    bike_data_Charger: '',
    bike_data_Remote: '',
    bike_data_Grips: '',
    bike_data_Spokes: '',
    bike_data_RimWidth: '',
    bike_data_RimDriver: '',
    bike_data_BrakePistons: '',
    bike_data_TireSize: '',
    bike_data_TireCompound: '',
    bike_data_TireTubeless: '',
    bike_data_ForkAxle: '',
    bike_data_ForkOffset: '',
    bike_data_ForkSteerer: '',
    bike_data_HandlebarWidth: '',
    bike_data_HandlebarRise: '',
    bike_data_HandlebarSweep: '',
    bike_data_ChainGuide: '',
    bike_data_Pedals: '',
    bike_data_RearHub: '',
    bike_data_FrontHub: '',
    bike_data_BottomBracket: '',
    bike_data_ForkMaterial: '',
    EnterCode: '',
    BikeType: '',
    BikeYear: '',
    bike_data_Gender: 'unisex',
    bike_data_Weight: '',
    bike_data_Fork: '',
    bike_data_Seatpost: '',
    bike_data_Saddle: '',
    bike_data_Handlebar: '',
    bike_data_Stem: '',
    bike_data_Headset: '',
    bike_data_Tires: '',
    bike_data_Rims: '',
    bike_data_Cassette: '',
    bike_data_Shifters: '',
    bike_data_Chain: '',
    bike_data_Crank: '',
    bike_data_Brakes: '',
    bike_data_BrakeType: '',
    bike_data_FrontDerailleur: '',
    bike_data_RearDerailleur: '',
    bike_data_FrameComponent: '',
    bike_data_SuspensionType: '',
    bike_data_Subcategory: '',
    EbikeElectricalCondition: '',
    bike_data_Shifting: '',
    Model: '',
    Brand: '',
    ProofOf: '',
    SerialNumber: '',
    PreviousOwners: '',
    LastService: '',
    bike_data_Drivetrain: '',
    bike_data_Groupset: '',
    bike_data_FrameMaterial: '',
    DamageDescription: '',
    DamagedParts: '',
    BikeFrameSize2: '',
    user_ReferralSourceOther: '',
    BikeSizing: '',
    bike_slug_brand: '',
    bike_slug_model: '',
    bike_slug_year: '',
    internal_upgraded_components: [],
    bike_data_Name: '',
    bike_data_RearShock: '',
    bike_data_SeatpostType: '',
    bike_data_TravelFront: '',
    bike_data_TravelRear: '',
    bike_data_WheelSize: '',
    bike_data_WheelMaterial: '',
    bike_data_Battery: '',
    bike_data_BatteryCapacity: '',
    bike_data_Motor: '',
    bike_data_Wheelset: '',
    bike_data_Year: '',
    internal_spec_groupset_brand: '',
    internal_spec_groupset_model: '',
    internal_spec_wheelset_brand: '',
    internal_spec_wheelset_model: '',
    internal_spec_fork_brand: '',
    internal_spec_fork_model: '',
    internal_spec_shock_brand: '',
    internal_spec_shock_model: '',
    internal_spec_brakes_brand: '',
    internal_spec_brakes_model: '',
    internal_spec_motor_brand: '',
    internal_spec_motor_model: '',
    internal_spec_brake_type: '',
    internal_spec_shifting: '',
    internal_spec_wheel_size: '',
    internal_spec_battery_capacity: '',
    bike_data_Category: '',
    bike_data_Rrp: '',
    bike_data_RrpGBP: '',
    bike_data_SuggestedPrice: '',
    internal_user_edited_price: false,
    internal_pricing_default_variant: '',
    EbikeChargerIncluded: '',
    EbikeChargerOriginal: '',
    EbikeChargerAmperage: '',
    EbikeBatteryHealth: '',
    EbikeBatteryHealthPercent: '',
    Subcategory: '',
    fork_type: '',
    fork_wheel_size: '',
    fork_travel: '',
    shock_type: '',
    shock_mounting: '',
    shock_length: '',
    shock_stroke: '',
    groupset_type: '',
    groupset_front_gears: '',
    groupset_rear_gears: '',
    shifter_type: '',
    shifter_front_gears: '',
    shifter_rear_gears: '',
    crank_material: '',
    chain_gears: '',
    cassette_gears: '',
    bottom_bracket_mounting: '',
    chainring_mounting: '',
    derailleur_type: '',
    derailleur_rear_gears: '',
    derailleur_front_mounting: '',
    brake_type: '',
    brake_type2: '',
    brake_mounting: '',
    brake_lever_type: '',
    headset_design: '',
    handlebar_design: '',
    handlebar_material: '',
    stem_length: '',
    stem_clamp_dimension: '',
    seatpost_type: '',
    seatpost_diameter: '',
    tyre_wheel_size: '',
    tyre_width: '',
    wheel_type: '',
    wheel_type2: '',
    wheel_size: '',
    wheel_diameter: '',
    pedal_type: '',
    year: '',
    sponsoredListing: false,
    contact_Enquiry: '',
    product_id: '',
    product_slug: '',
    tracking_clid: '',
    tracking_clid_type: '',
    tracking_client_id: '',
    tracking_service: '',
    tracking_ttp: '',
    tracking_fbp: '',
    tracking_distinct_id: '',
    tracking_session_id: '',
    source: ''
  };

  // Mirrors src/components/react/v3/upgradeComponents.ts for the components the
  // Ink form offers. `fields` are the bike_data_* keys whose values describe the
  // replacement part; UpgradeComponentSelector.tsx joins them into
  // ChangedPartsDescription on every toggle and keystroke. The primary list is
  // always visible; the secondary list hides behind the "Show less common
  // upgrades" toggle (data-upgrade-secondary-toggle).
  var upgradeComponents = [
    { key: 'Groupset', label: 'Groupset', fields: ['bike_data_Groupset', 'bike_data_Drivetrain', 'bike_data_Shifting'] },
    { key: 'Rear Derailleur', label: 'Rear Derailleur', fields: ['bike_data_RearDerailleur'] },
    { key: 'Front Derailleur', label: 'Front Derailleur', fields: ['bike_data_FrontDerailleur'] },
    { key: 'Shifters', label: 'Shifters', fields: ['bike_data_Shifters'] },
    { key: 'Cassette', label: 'Cassette', fields: ['bike_data_Cassette'] },
    { key: 'Crank', label: 'Crank', fields: ['bike_data_Crank'] },
    { key: 'Chain', label: 'Chain', fields: ['bike_data_Chain'] },
    { key: 'Brakes', label: 'Brakes', fields: ['bike_data_Brakes', 'bike_data_BrakeType'] },
    { key: 'Rims', label: 'Rims', fields: ['bike_data_Rims', 'bike_data_RimWidth'] },
    { key: 'Spokes', label: 'Spokes', fields: ['bike_data_Spokes'] },
    { key: 'Front Hub', label: 'Front Hub', fields: ['bike_data_FrontHub'] },
    { key: 'Rear Hub', label: 'Rear Hub', fields: ['bike_data_RearHub'] },
    { key: 'Tires', label: 'Tires', fields: ['bike_data_Tires', 'bike_data_TireSize', 'bike_data_TireCompound', 'bike_data_TireTubeless'] },
    { key: 'Handlebar', label: 'Handlebar', fields: ['bike_data_Handlebar', 'bike_data_HandlebarWidth', 'bike_data_HandlebarRise', 'bike_data_HandlebarSweep'] },
    { key: 'Stem', label: 'Stem', fields: ['bike_data_Stem'] },
    { key: 'Grips', label: 'Grips', fields: ['bike_data_Grips'] },
    { key: 'Saddle', label: 'Saddle', fields: ['bike_data_Saddle'] },
    { key: 'Seatpost', label: 'Seatpost', fields: ['bike_data_Seatpost'] },
    { key: 'Pedals', label: 'Pedals', fields: ['bike_data_Pedals'] }
  ];

  var secondaryUpgradeComponents = [
    { key: 'Bottom Bracket', label: 'Bottom Bracket', fields: ['bike_data_BottomBracket'] },
    { key: 'Chain Guide', label: 'Chain Guide', fields: ['bike_data_ChainGuide'] },
    { key: 'Brake Pistons', label: 'Brake Pistons', fields: ['bike_data_BrakePistons'] },
    { key: 'Fork', label: 'Fork', fields: ['bike_data_Fork', 'bike_data_ForkMaterial', 'bike_data_ForkAxle', 'bike_data_ForkOffset', 'bike_data_ForkSteerer'] },
    { key: 'Headset', label: 'Headset', fields: ['bike_data_Headset'] },
    { key: 'Rim Driver', label: 'Rim Driver', fields: ['bike_data_RimDriver'] }
  ];

  // All 25 components, primary first — the order composeChangedParts uses so a
  // description reads common upgrades before less common ones, exactly like the
  // React ALL_COMPONENTS = [...PRIMARY, ...SECONDARY].
  var allUpgradeComponents = upgradeComponents.concat(secondaryUpgradeComponents);

  var upgradeFieldKeys = allUpgradeComponents.reduce(function (keys, component) {
    component.fields.forEach(function (field) {
      if (keys.indexOf(field) === -1) keys.push(field);
    });
    return keys;
  }, []);

  function selectedUpgradeComponents(state) {
    var selected = (state || {}).internal_upgraded_components;
    if (!Array.isArray(selected)) return [];
    return allUpgradeComponents.filter(function (component) {
      return selected.indexOf(component.key) !== -1;
    });
  }

  function composeChangedParts(state) {
    return selectedUpgradeComponents(state).map(function (component) {
      var details = component.fields.map(function (field) {
        return String((state || {})[field] == null ? '' : (state || {})[field]).trim();
      }).filter(Boolean).join(', ');
      return details ? component.label + ': ' + details : component.label;
    }).join('. ');
  }

  // UpgradeComponentSelector.tsx:43-49 empties every field of a component the
  // seller unticks, so text typed for an upgrade they removed cannot survive as the
  // bike's factory specification. The store writes recompose ChangedPartsDescription.
  function clearUpgradeComponentFields(store, key) {
    var component = allUpgradeComponents.find(function (item) {
      return item.key === key;
    });
    if (!component || !store) return;
    component.fields.forEach(function (field) {
      if (store.get()[field] !== '') store.set(field, '');
    });
  }

  function isUpgradeKey(key) {
    return key === 'internal_upgraded_components'
      || key === 'UpgradesReplacements'
      || upgradeFieldKeys.indexOf(key) !== -1;
  }

  function clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function fallbackCapture(event, properties) {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(event, properties || {});
      }
    } catch (_error) {}
  }

  function integrationsAdapter() {
    return modules.integrations || { capture: fallbackCapture };
  }

  function captureEvent(event, properties) {
    var integrations = integrationsAdapter();
    if (integrations && typeof integrations.capture === 'function') integrations.capture(event, properties || {});
    else fallbackCapture(event, properties);
  }

  function moduleContext(root, store, config) {
    return {
      root: root,
      store: store,
      config: config || {},
      integrations: integrationsAdapter()
    };
  }

  function initializeCapability(capability, root, store, config) {
    if (capability && typeof capability.init === 'function') {
      capability.init(moduleContext(root, store, config));
    }
  }

  function register(name, capability) {
    modules[name] = capability;
    if (bootedStore) initializeCapability(capability, bootedRoot, bootedStore, bootedConfig);
  }

  function initializeModules(root, store, config) {
    Object.keys(modules).forEach(function (name) {
      initializeCapability(modules[name], root, store, config);
    });
  }

  function createStore(initial) {
    var state = clone(initial || {});
    var listeners = [];
    return {
      get: function () {
        return state;
      },
      set: function (key, value) {
        if (Object.is(state[key], value)) return;
        state[key] = value;
        listeners.slice().forEach(function (fn) {
          fn(state, key);
        });
      },
      patch: function (values) {
        Object.keys(values || {}).forEach(function (key) {
          this.set(key, values[key]);
        }, this);
      },
      subscribe: function (fn) {
        listeners.push(fn);
        return function () {
          listeners = listeners.filter(function (item) {
            return item !== fn;
          });
        };
      }
    };
  }

  function cleanMediaPair(state, metaKey, urlsKey, previewKey, now) {
    if (!Array.isArray(state[metaKey])) return;

    var urls = Array.isArray(state[urlsKey]) ? state[urlsKey] : [];
    var previews = Array.isArray(state[previewKey]) ? state[previewKey] : [];
    var expired = [];

    state[metaKey] = state[metaKey].filter(function (meta, index) {
      if (meta && meta.timestamp && now - meta.timestamp > THREE_DAYS) {
        expired.push(index);
        return false;
      }
      return true;
    });

    expired.reverse().forEach(function (index) {
      urls.splice(index, 1);
      previews.splice(index, 1);
    });

    state[urlsKey] = urls;
    state[previewKey] = previews;
  }

  function expireMedia(state) {
    var now = Date.now();
    cleanMediaPair(state, 'internal_media_drive', 'media_drive', 'internal_media_drive_preview', now);
    cleanMediaPair(state, 'internal_media_additional', 'media_additional', 'internal_media_additional_preview', now);
    cleanMediaPair(state, 'internal_media_side', 'media_side', 'internal_media_side_preview', now);
    cleanMediaPair(state, 'internal_media_drive_close', 'media_drive_close', 'internal_media_drive_close_preview', now);
    return state;
  }

  function loadDraft(storage) {
    if (!storage) return clone(blankForm);
    try {
      var raw = storage.getItem(FORM_KEY);
      if (!raw) return clone(blankForm);
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return clone(blankForm);
      // Upload requests cannot survive a reload, even if a draft was saved
      // while the current batch was processing.
      return expireMedia(Object.assign(clone(blankForm), parsed, { internal_processing_files: false }));
    } catch (_error) {
      return clone(blankForm);
    }
  }

  function persistDraft(storage, state) {
    if (!storage) return;
    try {
      storage.setItem(FORM_KEY, JSON.stringify(state));
    } catch (_error) {
      // Storage may be full or unavailable; form input should still work.
    }
  }

  function storageKeys(storage) {
    var seen = Object.create(null);
    var keys = [];
    if (!storage) return keys;

    Object.keys(storage).forEach(function (key) {
      seen[key] = true;
      keys.push(key);
    });

    if (typeof storage.length === 'number' && typeof storage.key === 'function') {
      for (var index = 0; index < storage.length; index += 1) {
        var storageKey = storage.key(index);
        if (storageKey !== null && !seen[storageKey]) keys.push(storageKey);
      }
    }

    return keys;
  }

  function clearDraft(storage) {
    if (isDebugMode()) return;
    storageKeys(storage).filter(function (key) {
      return key.indexOf('mnb:new-listing:') === 0;
    }).forEach(function (key) {
      storage.removeItem(key);
    });
  }

  function isDebugMode() {
    try {
      return window.sessionStorage && window.sessionStorage.getItem('debug_mode') === 'true';
    } catch (_error) {
      return false;
    }
  }

  function normalizeEmail(value) {
    return String(value || '').trim().toLowerCase().replace(/\s/g, '');
  }

  function resolveSafeRedirect(value, locationObj) {
    var target = locationObj || window.location;
    var url = new URL(value, target && target.origin ? target.origin : window.location.origin);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') throw new Error('Unsafe redirect protocol');
    return url.href;
  }

  function safeRedirect(value, locationObj) {
    var target = locationObj || window.location;
    target.assign(resolveSafeRedirect(value, target));
  }

  function resolveRate(price) {
    return price >= 2640 ? 0.12 : price >= 1740 ? 0.13 : 0.14;
  }

  function applyCommission(price) {
    return Math.round(price / (1 - resolveRate(price)));
  }

  function getCommissionPercent(price) {
    return Math.round(resolveRate(price) * 100);
  }

  function parseHydration(search) {
    var params = new URLSearchParams(search);
    var conditions = {
      'as-good-as-new': 'As good as new',
      'good-condition': 'Good condition',
      'needs-some-work': 'Needs some work'
    };
    var year = params.get('year');
    var rrp = params.get('rrp');
    var currentYear = new Date().getFullYear();

    if (!params.get('brand') || !params.get('model') || !conditions[params.get('condition')]) return null;
    if (!/^\d+$/.test(year || '') || Number(year) < 1990 || Number(year) > currentYear + 1) return null;
    if (!/^\d+$/.test(rrp || '') || Number(rrp) <= 0) return null;

    return {
      brandSlug: params.get('brand'),
      modelSlug: params.get('model'),
      modelManual: params.get('modelManual') === '1',
      year: year,
      condition: conditions[params.get('condition')],
      rrp: Number(rrp)
    };
  }

  function hydrationToForm(payload) {
    if (!payload) return {};
    return {
      Brand: payload.brandSlug,
      Model: payload.modelSlug,
      BikeYear: payload.year,
      condition: payload.condition,
      bike_data_Rrp: String(payload.rrp),
      bike_data_RrpGBP: String(payload.rrp),
      bike_slug_brand: payload.brandSlug,
      bike_slug_model: payload.modelManual ? '' : payload.modelSlug,
      bike_slug_year: payload.year,
      internal_listWithoutTemplate: payload.modelManual === true,
      source: 'valuation-tool'
    };
  }

  function removeHydrationParams() {
    if (!window.location || !window.history || !window.URL) return;
    var url = new URL(window.location.href);
    ['brand', 'model', 'modelManual', 'year', 'condition', 'rrp'].forEach(function (key) {
      url.searchParams.delete(key);
    });
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }

  function processFormData(state) {
    var composedState = Object.assign({}, state || {}, composeSpecAnswers(state || {}, {
      includeBlankRequired: state && state.internal_listWithoutTemplate === true
    }));
    // The selector owns ChangedPartsDescription end to end: there is no free-text
    // control, so answering anything but "Yes" must not leave a stale description.
    composedState.ChangedPartsDescription = composedState.UpgradesReplacements === 'Yes'
      ? composeChangedParts(composedState)
      : '';
    var result = {};
    Object.keys(composedState || {}).forEach(function (key) {
      if (key.indexOf('internal_') !== 0) result[key] = clone(composedState[key]);
    });
    result.listWithoutTemplate = composedState && composedState.internal_listWithoutTemplate === true;
    // Every other internal_ key is stripped above, but the server has to know
    // the seller took the assisted path: it is the only reason Brand, Model and
    // BikeYear are allowed to arrive blank.
    result.assistedIdentification = composedState && composedState.internal_assisted_identification === true;
    if (!result.media_drive.length) {
      result.media_drive = [result.media_additional[0]];
      result.media_additional = result.media_additional.slice(1);
    }
    var basePrice = parseFloat(result.ListingPrice);
    if (!isNaN(basePrice) && basePrice > 0) result.ListingPrice = String(applyCommission(basePrice));
    return result;
  }

  function prepareSubmissionPayload(state) {
    var payload = processFormData(state);
    if (payload.user_Email) payload.user_Email = normalizeEmail(payload.user_Email);
    return payload;
  }

  function collectListingTrackingData() {
    // Suppress the PostHog distinct id too when the ad-tracking global hasn't
    // loaded, so a partial-tracking environment submits fully blank fields
    // rather than a distinct id with no attribution data to pair it with.
    var hasTrackingSystem = window.mnbGetTrackingData;
    var trackingData = hasTrackingSystem
      ? window.mnbGetTrackingData('G-TFYK3QM8GX')
      : { clientId: '', ttp: '', fbp: '', clid: null, clidType: null, service: null };
    var distinctId = hasTrackingSystem && window.posthog && typeof window.posthog.get_distinct_id === 'function'
      ? window.posthog.get_distinct_id() : '';
    return {
      tracking_client_id: trackingData.clientId || '',
      tracking_clid: trackingData.clid || '',
      tracking_clid_type: trackingData.clidType || '',
      tracking_service: trackingData.service || '',
      tracking_ttp: trackingData.ttp || '',
      tracking_fbp: trackingData.fbp || '',
      tracking_distinct_id: distinctId || '',
      tracking_session_id: trackingData.sessionId || '',
      // listing-submission only forwards hashed customer identifiers to Google
      // on an explicit 'granted'; 'unknown' is treated exactly like 'denied'.
      tracking_consent_marketing: typeof window.mnbGetConsentState === 'function'
        ? window.mnbGetConsentState()
        : 'unknown'
    };
  }

  function numericPrice(value) {
    return parseFloat(String(value || '').replace(/[^0-9.]/g, '')) || 0;
  }

  function submissionProperties(state) {
    var listingPrice = numericPrice((state || {}).ListingPrice);
    var suggestedPrice = numericPrice((state || {}).bike_data_SuggestedPrice);
    return {
      listing_price: listingPrice,
      suggested_price: suggestedPrice,
      user_edited_price: (state || {}).internal_user_edited_price === true,
      price_diff: suggestedPrice > 0 ? listingPrice - suggestedPrice : 0,
      price_diff_pct: suggestedPrice > 0 ? Math.round(((listingPrice - suggestedPrice) / suggestedPrice) * 100) : 0,
      pricing_default_variant: (state || {}).internal_pricing_default_variant || '',
      list_without_template: (state || {}).internal_listWithoutTemplate === true
    };
  }

  function formatGBP(value) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0
    }).format(Number(value) || 0);
  }

  function computePricing(state) {
    var raw = numericPrice((state || {}).ListingPrice);
    var commissionPercent = raw > 0 ? getCommissionPercent(raw) : 0;
    return {
      rawPrice: raw,
      displayPrice: raw > 0 ? applyCommission(raw) : 0,
      commissionPercent: commissionPercent
    };
  }

  function bindPreview(root, store) {
    var scope = root || document.querySelector('[data-new-listing-root]');
    var previewEl = scope && scope.querySelector('mnb-listing-preview');
    var opener = scope && scope.querySelector('[data-preview-open]');
    if (!previewEl || !store || typeof previewEl.update !== 'function') return;

    previewEl.update(store.get(), computePricing(store.get()));
    store.subscribe(function (state) {
      previewEl.update(state, computePricing(state));
    });

    // The preview's sponsored badge carries a dismiss (×) control — the
    // counterpart to the Contact section's [data-sponsor-boost]. Clicking it
    // flips sponsoredListing back to false: the store subscription re-renders
    // the preview without the chip and re-shows the upsell panel.
    previewEl.addEventListener('mnb:remove-sponsored', function () {
      store.set('sponsoredListing', false);
    });

    if (opener && opener.getAttribute('data-preview-bound') !== 'true') {
      opener.setAttribute('data-preview-bound', 'true');
      opener.addEventListener('click', function () {
        previewEl.openDrawer();
      });
    }
  }

  function text(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function editDistance(left, right) {
    var previousRow = [];
    var leftIndex;
    var rightIndex;
    var previousDiagonal;
    var current;

    for (rightIndex = 0; rightIndex <= right.length; rightIndex += 1) previousRow[rightIndex] = rightIndex;

    for (leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
      previousDiagonal = previousRow[0] || 0;
      previousRow[0] = leftIndex;

      for (rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
        current = previousRow[rightIndex] || 0;
        previousRow[rightIndex] = Math.min(
          (previousRow[rightIndex] || 0) + 1,
          (previousRow[rightIndex - 1] || 0) + 1,
          previousDiagonal + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1)
        );
        previousDiagonal = current;
      }
    }

    return previousRow[right.length] || 0;
  }

  function minArrayLength(count) {
    return function (value) {
      if (!Array.isArray(value)) return 'Must be provided';
      return value.length >= count ? null : 'At least ' + count + ' items required';
    };
  }

  function maxLength(max) {
    return function (value) {
      if (!value) return null;
      return String(value).length <= max ? null : 'Maximum ' + max + ' characters allowed';
    };
  }

  function minLength(min) {
    return function (value) {
      if (!value) return null;
      return String(value).length >= min ? null : 'At least ' + min + ' characters required';
    };
  }

  function conditional(condition, validator) {
    return function (value, form) {
      return condition(form) ? validator(value, form) : null;
    };
  }

  var validators = {
    required: function (value) {
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length === 0)
      ) {
        return 'This field is required';
      }
      return null;
    },
    email: function (value) {
      if (!value) return null;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).replace(/\s/g, '')) ? null : 'Please enter a valid email address';
    },
    emailDomainTypo: function (value) {
      var email = String(value || '').trim().toLowerCase().replace(/\s/g, '');
      var pieces = email.split('@');
      var domain = pieces[1];
      var commonDomains = [
        'gmail.com',
        'yahoo.com',
        'outlook.com',
        'hotmail.com',
        'live.com',
        'icloud.com',
        'aol.com',
        'protonmail.com',
        'btinternet.com',
        'virginmedia.com',
        'talktalk.net'
      ];
      var suggestedDomain;

      if (!domain || pieces.length !== 2) return null;
      suggestedDomain = commonDomains.find(function (commonDomain) {
        return editDistance(domain, commonDomain) === 1;
      });
      return suggestedDomain ? 'Did you mean ' + suggestedDomain + '?' : null;
    },
    phone: function (value) {
      if (!value) return null;
      // UK mobiles (07…) and landlines / non-geo voice numbers (01…, 02…, 03…),
      // in national or +44/44 form. The trunk "0" after the country code
      // ("+44 (0) 20 …") is repaired before matching. Premium-rate 08/09 and
      // non-UK numbers stay rejected — mirrors PhoneNumber.ts in mnb-edge.
      var digits = String(value).replace(/\D/g, '').replace(/^440+(?=[1237])/, '44');
      return /^(?:44|0)(?:7\d{9}|[123]\d{8,9})$/.test(digits) ? null : 'Please enter a valid UK phone number';
    },
    price: function (value) {
      var numValue;
      if (!value || value === '') return 'Price is required';
      numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue) || numValue <= 0) return 'Price must be a positive number';
      if (numValue < 135) return 'Price must be at least £135';
      if (numValue > 50000) return 'Price cannot exceed £50,000';
      return null;
    },
    minArrayLength: minArrayLength,
    maxLength: maxLength,
    minLength: minLength
  };

  var NOT_SURE = 'Not sure';
  var OTHER = 'Other';

  var GROUPSET_BRANDS = [
    'Shimano', 'SRAM', 'Campagnolo', 'FSA', 'microSHIFT', 'TRP', 'Rotor', 'Praxis', 'Box',
    OTHER, NOT_SURE
  ];
  var FORK_BRANDS = [
    'RockShox', 'Fox', 'Öhlins', 'Marzocchi', 'DVO', 'Manitou', 'X-Fusion', 'SR Suntour',
    'Cane Creek', 'DT Swiss', 'Formula', 'EXT',
    OTHER, NOT_SURE
  ];
  var SHOCK_BRANDS = [
    'RockShox', 'Fox', 'Öhlins', 'Cane Creek', 'DVO', 'Marzocchi', 'EXT', 'X-Fusion', 'Manitou',
    OTHER, NOT_SURE
  ];
  var BRAKE_BRANDS = [
    'Shimano', 'SRAM', 'Magura', 'Hope', 'TRP', 'Hayes', 'Formula', 'Trickstuff', 'Tektro',
    OTHER, NOT_SURE
  ];
  var MOTOR_BRANDS = [
    'Bosch', 'Shimano', 'Yamaha', 'Brose', 'Bafang', 'Fazua', 'Mahle', 'TQ', 'Panasonic',
    'Specialized', 'Giant SyncDrive', 'Hyena', 'Ananda',
    OTHER, NOT_SURE
  ];
  var WHEELSET_BRANDS = [
    'Shimano', 'DT Swiss', 'Mavic', 'Fulcrum', 'Campagnolo', 'Zipp', 'ENVE', 'Roval', 'Bontrager',
    'Giant', 'Hunt', 'Reynolds', 'Vision', 'Industry Nine', 'Race Face', 'Crankbrothers',
    'Stan\'s NoTubes', 'Hope', 'Halo',
    OTHER, NOT_SURE
  ];

  var specFields = [
    {
      id: 'groupset',
      kind: 'brandModel',
      label: 'Groupset',
      targetKey: 'bike_data_Groupset',
      brandKey: 'internal_spec_groupset_brand',
      modelKey: 'internal_spec_groupset_model',
      brandLabel: 'Which brand is the bike\'s groupset?',
      modelLabel: 'Which groupset is it?',
      modelPlaceholder: 'e.g. 105 Di2, GX Eagle',
      brands: GROUPSET_BRANDS
    },
    {
      id: 'brake_type',
      kind: 'choice',
      label: 'Brake type',
      targetKey: 'bike_data_BrakeType',
      answerKey: 'internal_spec_brake_type',
      placeholder: 'Select Brake Type',
      choices: ['Hydraulic disc', 'Mechanical disc', 'Rim', NOT_SURE]
    },
    {
      id: 'shifting',
      kind: 'choice',
      label: 'Shifting',
      targetKey: 'bike_data_Shifting',
      answerKey: 'internal_spec_shifting',
      placeholder: 'Select Shifting Type',
      choices: ['Electronic', 'Mechanical', NOT_SURE]
    },
    {
      id: 'wheelset',
      kind: 'brandModel',
      label: 'Wheelset',
      targetKey: 'bike_data_Wheelset',
      brandKey: 'internal_spec_wheelset_brand',
      modelKey: 'internal_spec_wheelset_model',
      brandLabel: 'Which brand are the bike\'s wheels?',
      modelLabel: 'Which wheelset is it?',
      modelPlaceholder: 'e.g. Ksyrium SL, 303 Firecrest',
      brands: WHEELSET_BRANDS
    },
    {
      id: 'wheel_size',
      kind: 'choice',
      label: 'Wheel size',
      targetKey: 'bike_data_WheelSize',
      answerKey: 'internal_spec_wheel_size',
      placeholder: 'Select Wheel Size',
      choices: ['26"', '27.5"', '29"', 'Mixed (mullet)', NOT_SURE]
    },
    {
      id: 'fork',
      kind: 'brandModel',
      label: 'Fork',
      targetKey: 'bike_data_Fork',
      brandKey: 'internal_spec_fork_brand',
      modelKey: 'internal_spec_fork_model',
      brandLabel: 'Which brand is the bike\'s fork?',
      modelLabel: 'Which fork is it?',
      modelPlaceholder: 'e.g. Pike Ultimate, 36 Factory',
      brands: FORK_BRANDS
    },
    {
      id: 'shock',
      kind: 'brandModel',
      label: 'Rear shock',
      targetKey: 'bike_data_RearShock',
      brandKey: 'internal_spec_shock_brand',
      modelKey: 'internal_spec_shock_model',
      brandLabel: 'Which brand is the bike\'s rear shock?',
      modelLabel: 'Which rear shock is it?',
      modelPlaceholder: 'e.g. Super Deluxe Ultimate, Float X2',
      brands: SHOCK_BRANDS
    },
    {
      id: 'brakes',
      kind: 'brandModel',
      label: 'Brakes',
      targetKey: 'bike_data_Brakes',
      brandKey: 'internal_spec_brakes_brand',
      modelKey: 'internal_spec_brakes_model',
      brandLabel: 'Which brand are the bike\'s brakes?',
      modelLabel: 'Which brakes are they?',
      modelPlaceholder: 'e.g. XT M8120, Code RSC',
      brands: BRAKE_BRANDS
    },
    {
      id: 'motor',
      kind: 'brandModel',
      label: 'Motor',
      targetKey: 'bike_data_Motor',
      brandKey: 'internal_spec_motor_brand',
      modelKey: 'internal_spec_motor_model',
      brandLabel: 'Which brand is the bike\'s motor?',
      modelLabel: 'Which motor is it?',
      modelPlaceholder: 'e.g. Performance Line CX, EP8',
      brands: MOTOR_BRANDS
    },
    {
      id: 'battery_capacity',
      kind: 'choice',
      label: 'Battery capacity',
      targetKey: 'bike_data_BatteryCapacity',
      answerKey: 'internal_spec_battery_capacity',
      placeholder: 'Select Battery Capacity',
      choices: [
        '250Wh', '300Wh', '320Wh', '360Wh', '400Wh', '418Wh', '430Wh', '500Wh', '504Wh', '540Wh',
        '545Wh', '600Wh', '625Wh', '630Wh', '700Wh', '720Wh', '750Wh', '800Wh', '900Wh',
        NOT_SURE
      ]
    }
  ];

  var specRequirements = {
    road: ['groupset', 'brake_type', 'shifting', 'wheelset'],
    mountain: ['wheel_size', 'fork', 'shock', 'groupset', 'brakes'],
    electric: ['motor', 'battery_capacity', 'groupset'],
    other: []
  };

  function resolveBikeSpecCategory(form) {
    var bikeType = text(form.BikeType);
    var category = text(form.bike_data_Category);
    var subcategory = text(form.bike_data_Subcategory);
    var haystack = bikeType + ' ' + category + ' ' + subcategory;
    var roadSubcategories = /^(endurance|aero|race|gravel|triathlon|cyclocross|touring)$/i;
    var mountainSubcategories = /^(trail|cross ?country|enduro|downhill|freeride)$/i;

    if (
      text(form.internal_electric_bike) === 'Yes' ||
      text(form.bike_data_Motor).length > 0 ||
      /electric|e-?bike|e-?mtb/i.test(haystack)
    ) {
      return 'electric';
    }

    if (/mountain|mtb/i.test(haystack) || mountainSubcategories.test(subcategory)) return 'mountain';
    if (/road|gravel|cyclocross/i.test(haystack) || roadSubcategories.test(subcategory)) return 'road';
    return 'other';
  }

  function requiredSpecFields(form) {
    if ((form || {}).internal_assisted_identification === true) return [];
    var requiredIds = specRequirements[resolveBikeSpecCategory(form)] || [];
    return requiredIds.map(function (id) {
      return specFields.find(function (field) {
        return field.id === id;
      });
    }).filter(Boolean);
  }

  function allSpecFields() {
    var seen = {};
    var fields = [];
    Object.keys(specRequirements).forEach(function (category) {
      specRequirements[category].forEach(function (id) {
        var field;
        if (seen[id]) return;
        seen[id] = true;
        field = specFields.find(function (item) {
          return item.id === id;
        });
        if (field) fields.push(field);
      });
    });
    return fields;
  }

  function composeSpecValue(brand, model) {
    var trimmedBrand = text(brand);
    var trimmedModel = text(model);
    if (trimmedBrand === NOT_SURE || trimmedBrand === '') return '';
    if (trimmedBrand === OTHER) return trimmedModel;
    return (trimmedBrand + ' ' + trimmedModel).trim();
  }

  function stripNotSure(value) {
    return text(value) === NOT_SURE ? '' : value;
  }

  function composeSpecFieldAnswer(field, form) {
    var answer = {};
    if (field.kind === 'choice') {
      answer[field.targetKey] = stripNotSure(form[field.answerKey]);
      return answer;
    }
    answer[field.targetKey] = composeSpecValue(form[field.brandKey], form[field.modelKey]);
    return answer;
  }

  function composeSpecAnswers(form, options) {
    var answers = {};
    var includeBlankIds = {};
    if (options && options.includeBlankRequired) {
      requiredSpecFields(form || {}).forEach(function (field) {
        includeBlankIds[field.id] = true;
      });
    }
    specFields.forEach(function (field) {
      if (field.kind === 'choice') {
        if (includeBlankIds[field.id] || text(form[field.answerKey]).length > 0) {
          Object.assign(answers, composeSpecFieldAnswer(field, form));
        }
        return;
      }
      if (includeBlankIds[field.id] || text(form[field.brandKey]).length > 0 || text(form[field.modelKey]).length > 0) {
        Object.assign(answers, composeSpecFieldAnswer(field, form));
      }
    });
    return answers;
  }

  function specFieldForAnswerKey(key) {
    return specFields.find(function (field) {
      return field.kind === 'choice'
        ? field.answerKey === key
        : field.brandKey === key || field.modelKey === key;
    });
  }

  function isSpecFieldIncomplete(field, form) {
    var brand;
    if (field.kind === 'choice') return text(form[field.targetKey]).length === 0 && text(form[field.answerKey]).length === 0;
    brand = text(form[field.brandKey]);
    if (brand.length === 0) return text(form[field.targetKey]).length === 0;
    if (brand === 'Not sure') return false;
    return text(form[field.modelKey]).length === 0;
  }

  function specRequirementRules() {
    return specFields.map(function (field) {
      return {
        field: field.kind === 'choice' ? field.answerKey : field.brandKey,
        validate: function (_value, form) {
          var applies = requiredSpecFields(form).some(function (required) {
            return required.id === field.id;
          });
          if (!applies) return null;
          return isSpecFieldIncomplete(field, form) ? field.label + ' is required' : null;
        },
        required: true
      };
    });
  }

  var validationRuleGroups = {
    'bike-valuation': [
      {
        field: 'Brand',
        validate: conditional(function (form) { return form.internal_assisted_identification !== true; }, validators.required),
        required: true
      },
      {
        field: 'Model',
        validate: conditional(function (form) { return form.internal_assisted_identification !== true; }, validators.required),
        required: true
      },
      {
        field: 'BikeYear',
        validate: conditional(function (form) {
          return form.internal_assisted_identification !== true && form.internal_year_not_sure !== true;
        }, validators.required),
        required: true
      },
      {
        field: 'bike_data_Rrp',
        validate: conditional(function (form) {
          return form.internal_listWithoutTemplate === true && form.internal_assisted_identification !== true;
        }, validators.required),
        required: true
      },
      {
        field: 'BikeType',
        validate: conditional(function (form) { return form.internal_assisted_identification === true; }, validators.required),
        required: true
      },
      {
        field: 'bike_identification_frame_text',
        validate: conditional(function (form) { return form.internal_assisted_identification === true; }, validators.required),
        required: true
      },
      {
        field: 'bike_identification_age_range',
        validate: conditional(function (form) { return form.internal_assisted_identification === true; }, validators.required),
        required: true
      },
      {
        field: 'bike_identification_price_band',
        validate: conditional(function (form) { return form.internal_assisted_identification === true; }, validators.required),
        required: true
      },
      { field: 'condition', validate: validators.required, required: true }
    ],
    'bike-specifications': [
      { field: 'BikeSizing', validate: validators.required, required: true },
      {
        field: 'internal_electric_bike',
        validate: conditional(function (form) {
          return form.internal_listWithoutTemplate === true && form.internal_assisted_identification !== true;
        }, validators.required),
        required: true
      },
      {
        field: 'BikeType',
        validate: conditional(function (form) {
          return form.internal_listWithoutTemplate === true && form.internal_electric_bike !== 'Yes';
        }, validators.required),
        required: true
      },
      {
        field: 'bike_data_Subcategory',
        validate: conditional(function (form) {
          return form.internal_listWithoutTemplate === true && form.internal_assisted_identification !== true;
        }, validators.required),
        required: true
      },
      {
        field: 'bike_data_WheelSize',
        validate: conditional(function (form) {
          return form.internal_listWithoutTemplate === true && form.internal_assisted_identification !== true &&
            form.internal_electric_bike === 'No' && form.BikeType === 'Youth';
        }, validators.required),
        required: true
      },
      { field: 'Height', validate: validators.required, required: true },
      { field: 'BikeColour', validate: validators.required, required: true }
    ].concat(specRequirementRules()),
    pricing: [
      {
        field: 'ListingPrice',
        validate: function (value) {
          if (typeof value !== 'number' && typeof value !== 'string') return 'Price is required';
          return validators.price(value);
        },
        required: true
      }
    ],
    photos: [
      {
        field: 'media_additional',
        validate: function (value) {
          var minimumError = validators.minArrayLength(3)(value);
          if (minimumError) return minimumError;
          return value.length > MAX_PHOTOS ? 'Maximum ' + MAX_PHOTOS + ' photos allowed' : null;
        },
        required: true
      }
    ],
    'additional-details': [
      {
        field: 'DamageDescription',
        validate: conditional(function (form) {
          return form.condition === 'Needs some work';
        }, validators.required),
        required: true
      },
      { field: 'BikeShortDescription', validate: validators.required, required: true },
      { field: 'PreviousOwners', validate: validators.required, required: true },
      { field: 'LastService', validate: validators.required, required: true }
    ],
    'contact-information': [
      { field: 'user_NameFirst', validate: validators.required, required: true },
      { field: 'user_NameLast', validate: validators.required, required: true },
      {
        field: 'user_Email',
        validate: function (value) {
          var requiredCheck = validators.required(value);
          if (requiredCheck) return requiredCheck;
          if (typeof value !== 'string') return 'Please enter a valid email address';
          return validators.email(value);
        },
        required: true
      },
      {
        field: 'user_Email',
        validate: function (value) {
          return typeof value === 'string' ? validators.emailDomainTypo(value) : null;
        },
        required: false
      },
      {
        field: 'user_PhoneNumber',
        validate: function (value) {
          var requiredCheck = validators.required(value);
          if (requiredCheck) return requiredCheck;
          if (typeof value !== 'string') return 'Please enter a valid phone number';
          return validators.phone(value);
        },
        required: true
      }
    ],
    address: [
      { field: 'address_house_number', validate: validators.required, required: true },
      { field: 'address_street_name', validate: validators.required, required: true },
      { field: 'address_postcode', validate: validators.required, required: true },
      { field: 'address_countryCode', validate: validators.required, required: true }
    ]
  };

  var sectionRuleGroups = {
    'bike-valuation': ['bike-valuation', 'pricing'],
    'bike-specifications': ['bike-specifications'],
    photos: ['photos'],
    'additional-details': ['additional-details'],
    'contact-information': ['contact-information', 'address']
  };

  var sectionOrder = [
    'bike-valuation',
    'bike-specifications',
    'photos',
    'additional-details',
    'contact-information'
  ];

  function runRules(rules, state) {
    var errors = {};
    var warnings = {};
    (rules || []).forEach(function (rule) {
      var value = state ? state[rule.field] : undefined;
      var error = rule.validate(value, state || {});
      if (!error) return;
      if (rule.required) errors[rule.field] = error;
      else warnings[rule.field] = error;
    });
    return {
      isValid: Object.keys(errors).length === 0,
      errors: errors,
      warnings: warnings
    };
  }

  function mergeValidation(target, source) {
    Object.assign(target.errors, source.errors);
    Object.assign(target.warnings, source.warnings);
    target.isValid = target.isValid && source.isValid;
    return target;
  }

  function validateSection(id, state) {
    var groupIds = sectionRuleGroups[id] || (validationRuleGroups[id] ? [id] : []);
    return groupIds.reduce(function (result, groupId) {
      return mergeValidation(result, runRules(validationRuleGroups[groupId], state));
    }, { isValid: true, errors: {}, warnings: {} });
  }

  function validateForm(state) {
    var sections = {};
    var result = { isValid: true, errors: {}, warnings: {}, sections: sections };
    sectionOrder.forEach(function (id) {
      sections[id] = validateSection(id, state);
      mergeValidation(result, sections[id]);
    });
    return result;
  }

  function readConfig() {
    var node = document.getElementById('NewListingConfig');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (_error) {
      return {};
    }
  }

  function readArrayInput(value) {
    if (!value) return [];
    try {
      var parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch (_error) {
      // Fall through to comma-separated handling for plain hidden values.
    }
    return String(value).split(',').map(function (item) {
      return item.trim();
    }).filter(Boolean);
  }

  function readControl(control, state) {
    var current = state ? state[control.getAttribute('data-field')] : undefined;
    var currentArray;

    if (control.multiple && control.options) {
      return Array.prototype.slice.call(control.selectedOptions).map(function (option) {
        return option.value;
      });
    }

    if (control.hasAttribute('data-array')) {
      if (control.type === 'checkbox') {
        currentArray = Array.isArray(current) ? current.slice() : [];
        if (control.checked && currentArray.indexOf(control.value) === -1) currentArray.push(control.value);
        if (!control.checked) currentArray = currentArray.filter(function (item) {
          return item !== control.value;
        });
        return currentArray;
      }
      return readArrayInput(control.value);
    }

    if (control.type === 'checkbox') return control.checked;
    if (control.type === 'radio') return control.checked ? control.value : current;
    if (control.type === 'number') return control.value === '' ? '' : Number(control.value);
    return control.value;
  }

  function writeControl(control, value) {
    var arrayValue;
    if (control.multiple && control.options) {
      arrayValue = Array.isArray(value) ? value : [];
      Array.prototype.slice.call(control.options).forEach(function (option) {
        option.selected = arrayValue.indexOf(option.value) !== -1;
      });
      return;
    }

    if (control.hasAttribute('data-array')) {
      arrayValue = Array.isArray(value) ? value : [];
      if (control.type === 'checkbox') control.checked = arrayValue.indexOf(control.value) !== -1;
      else control.value = JSON.stringify(arrayValue);
      return;
    }

    if (control.type === 'checkbox') {
      control.checked = value === true;
    } else if (control.type === 'radio') {
      control.checked = String(value || '') === control.value;
    } else {
      control.value = value == null ? '' : String(value);
    }
  }

  // Field errors are conveyed by the highlighted border alone — the inline
  // message text that used to sit below each label has been removed. An
  // invalid control carries aria-invalid="true" so the field gets the signal
  // accent border; a warning keeps the field quiet (aria-invalid="false") so
  // nothing is flagged until the seller actually needs to fix something.
  function setControlError(control, message, warning) {
    if (message) {
      control.setAttribute('aria-invalid', warning ? 'false' : 'true');
    } else {
      control.removeAttribute('aria-invalid');
    }
  }

  function renderErrorSummary(root, validation) {
    var summary = root && root.querySelector('[data-error-summary]');
    var fields;
    var list;

    if (!summary) return;
    fields = Object.keys(validation.errors || {});
    summary.textContent = '';
    if (!fields.length) {
      summary.hidden = true;
      return;
    }

    summary.hidden = false;
    summary.appendChild(document.createTextNode('Please fix the following fields:'));
    list = document.createElement('ul');
    fields.forEach(function (field) {
      var item = document.createElement('li');
      var link = document.createElement('a');
      var control = root.querySelector('[data-field="' + field + '"]');
      if (control && !control.id) control.id = 'nl-field-' + field.replace(/[^a-z0-9_-]+/gi, '-');
      link.href = control && control.id ? '#' + control.id : '#';
      link.textContent = validation.errors[field];
      link.addEventListener('click', function (event) {
        event.preventDefault();
        if (control) focusControl(root, control);
      });
      item.appendChild(link);
      list.appendChild(item);
    });
    summary.appendChild(list);
  }

  // AccordionSection.tsx:49-50 — errors stay hidden until the section has been
  // opened. The section open right now always counts as opened.
  function sectionRevealed(id, activeId) {
    return Boolean(id) && (id === activeId || Boolean(revealedSections[id]));
  }

  function renderSectionState(root, state, activeId) {
    sectionOrder.forEach(function (id) {
      var section = root.querySelector('[data-section-id="' + id + '"]');
      var trigger = section && section.querySelector('[data-section-trigger]');
      var status = section && section.querySelector('[data-section-status]');
      var result = validateSection(id, state);
      var errorCount = sectionRevealed(id, activeId) ? Object.keys(result.errors).length : 0;

      if (!section) return;
      section.setAttribute('data-section-state', result.isValid ? 'complete' : 'incomplete');
      if (status) status.textContent = result.isValid ? 'Complete' : 'Incomplete';
      if (trigger) trigger.setAttribute('aria-invalid', errorCount ? 'true' : 'false');
    });
  }

  function renderSpecControls(root, state) {
    var requiredIds = requiredSpecFields(state || {}).map(function (field) {
      return field.id;
    });
    var isManual = (state || {}).internal_listWithoutTemplate === true;
    var isAssisted = (state || {}).internal_assisted_identification === true;
    var showManualQuestions = isManual && !isAssisted;
    var electricAnswer = text((state || {}).internal_electric_bike);
    var bikeType = text((state || {}).BikeType);
    var upgrades = text((state || {}).UpgradesReplacements);
    var condition = text((state || {}).condition);
    var chargerIncluded = text((state || {}).EbikeChargerIncluded);
    var batteryHealth = text((state || {}).EbikeBatteryHealth);

    Array.prototype.slice.call(root.querySelectorAll('[data-manual-only]')).forEach(function (node) {
      node.hidden = !showManualQuestions;
    });
    Array.prototype.slice.call(root.querySelectorAll('[data-manual-nonelectric-only]')).forEach(function (node) {
      node.hidden = !(showManualQuestions && electricAnswer === 'No');
    });
    Array.prototype.slice.call(root.querySelectorAll('[data-manual-after-electric]')).forEach(function (node) {
      node.hidden = !(showManualQuestions && (electricAnswer === 'Yes' || electricAnswer === 'No'));
    });
    Array.prototype.slice.call(root.querySelectorAll('[data-manual-electric-only]')).forEach(function (node) {
      node.hidden = !(showManualQuestions && (electricAnswer === 'Yes' || bikeType === 'Electric'));
    });
    Array.prototype.slice.call(root.querySelectorAll('[data-youth-only]')).forEach(function (node) {
      node.hidden = !(showManualQuestions && electricAnswer === 'No' && bikeType === 'Youth');
    });
    Array.prototype.slice.call(root.querySelectorAll('[data-upgrades-detail]')).forEach(function (node) {
      node.hidden = upgrades !== 'Yes';
    });
    var upgradedComponents = Array.isArray((state || {}).internal_upgraded_components) ? state.internal_upgraded_components : [];
    Array.prototype.slice.call(root.querySelectorAll('[data-upgrade-details]')).forEach(function (node) {
      node.hidden = !(upgrades === 'Yes' && upgradedComponents.length > 0);
    });
    Array.prototype.slice.call(root.querySelectorAll('[data-upgrade-detail-for]')).forEach(function (node) {
      node.hidden = !(upgrades === 'Yes' && upgradedComponents.indexOf(node.getAttribute('data-upgrade-detail-for')) !== -1);
    });
    Array.prototype.slice.call(root.querySelectorAll('[data-condition-value]')).forEach(function (node) {
      node.hidden = condition !== node.getAttribute('data-condition-value');
    });
    Array.prototype.slice.call(root.querySelectorAll('[data-electric-only]')).forEach(function (node) {
      node.hidden = isAssisted || electricAnswer !== 'Yes';
    });
    Array.prototype.slice.call(root.querySelectorAll('[data-ebike-charger-original]')).forEach(function (node) {
      node.hidden = isAssisted || !(electricAnswer === 'Yes' && chargerIncluded === 'Yes');
    });
    Array.prototype.slice.call(root.querySelectorAll('[data-ebike-battery-percent]')).forEach(function (node) {
      node.hidden = isAssisted || !(electricAnswer === 'Yes' && batteryHealth === 'No');
    });
    Array.prototype.slice.call(root.querySelectorAll('[data-spec-id]')).forEach(function (node) {
      var id = node.getAttribute('data-spec-id');
      var field = specFields.find(function (item) {
        return item.id === id;
      });
      var brand;
      var visible = requiredIds.indexOf(id) !== -1;
      node.hidden = !visible;

      if (field && field.kind === 'brandModel') {
        brand = text((state || {})[field.brandKey]);
        Array.prototype.slice.call(node.querySelectorAll('[data-spec-model-for="' + id + '"]')).forEach(function (modelNode) {
          modelNode.hidden = !(visible && brand.length > 0 && brand !== NOT_SURE);
        });
      }
    });

    // The "Category-required specifications" fieldset reserves 16px of top
    // padding even when every group inside it is hidden (before a category that
    // needs them is chosen, or one that needs none). Toggle the whole container
    // so an empty spec section doesn't leave a blank padded box on the page.
    var categorySpecs = root.querySelector('[data-category-required-specs]');
    if (categorySpecs) categorySpecs.hidden = requiredIds.length === 0;
  }

  // ContactInformationSection.tsx:86-137 — the "Boost Your Listing" upsell only
  // exists while sponsoredListing is false; boosting hides it and the preview
  // renders its "Sponsored listing" chip instead (new-listing-preview.js).
  // Removing it works the other way: the chip's × publishes
  // mnb:remove-sponsored, bindPreview flips sponsoredListing back to false,
  // and this panel reappears.
  // Also ported from ContactInformationSection.tsx:18-36,62-78: the inline
  // "Did you mean [domain]?" correction. It is a clickable suggestion, not just
  // a warning — the button rewrites user_Email to the corrected domain. The
  // domain-distance logic stays in validators.emailDomainTypo.
  function renderContactExtras(root, state) {
    var upsell = root && root.querySelector('[data-sponsor-upsell]');
    var suggestion = root && root.querySelector('[data-email-suggestion]');
    var apply = suggestion && suggestion.querySelector('[data-email-suggestion-apply]');
    var warning;
    var match;

    if (!root || !state) return;
    if (upsell) upsell.hidden = state.sponsoredListing === true;

    if (!suggestion || !apply) return;
    warning = validators.emailDomainTypo(String(state.user_Email || ''));
    match = warning ? warning.match(/^Did you mean (.+)\?$/) : null;
    if (match && match[1]) {
      apply.textContent = match[1];
      suggestion.hidden = false;
    } else {
      suggestion.hidden = true;
    }
  }

  function renderValidation(root, state, activeId, options) {
    var validation = validateForm(state);
    var controls = Array.prototype.slice.call(root.querySelectorAll('[data-field]'));

    renderSpecControls(root, state);

    controls.forEach(function (control) {
      var field = control.getAttribute('data-field');
      var message = touchedFields[field]
        ? validation.errors[field] || validation.warnings[field]
        : '';
      setControlError(control, message, Boolean(message && validation.warnings[field] && !validation.errors[field]));
    });

    renderSectionState(root, state, activeId);
    renderContactExtras(root, state);
    if ((options && options.summary) || (root.querySelector('[data-error-summary]') && !root.querySelector('[data-error-summary]').hidden)) {
      renderErrorSummary(root, validation);
    }
    return validation;
  }

  function firstInvalidSection(validation) {
    return sectionOrder.find(function (id) {
      return validation.sections && validation.sections[id] && Object.keys(validation.sections[id].errors).length;
    });
  }

  function focusControl(root, control) {
    var section = control.closest('[data-section-id]');
    if (section) openSection(section.getAttribute('data-section-id'), { root: root, focus: false });
    if (typeof control.focus === 'function' && control.type !== 'hidden') control.focus();
  }

  function focusFirstInvalid(root, validation) {
    var sectionId = firstInvalidSection(validation);
    var fields;
    var control;
    if (!sectionId) return;
    openSection(sectionId, { root: root, focus: false, scroll: true });
    fields = Object.keys(validation.sections[sectionId].errors);
    control = fields.map(function (field) {
      return root.querySelector('[data-section-id="' + sectionId + '"] [data-field="' + field + '"]');
    }).filter(Boolean)[0];
    if (control && control.type !== 'hidden' && typeof control.focus === 'function') {
      control.focus();
    } else {
      var trigger = root.querySelector('[data-section-id="' + sectionId + '"] [data-section-trigger]');
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
    }
  }

  function currentSectionId(root) {
    var openTrigger = root.querySelector('[data-section-trigger][aria-expanded="true"]');
    var section = openTrigger && openTrigger.closest('[data-section-id]');
    return section ? section.getAttribute('data-section-id') : sectionOrder[0];
  }

  function openSection(id, options) {
    var root = (options && options.root) || document.querySelector('[data-new-listing-root]');
    var section = root && root.querySelector('[data-section-id="' + id + '"]');
    var trigger;
    var scrollBehavior;
    var scrollContainer;
    var sectionIndex;

    if (!root || !section) return;

    sectionOrder.forEach(function (sectionId) {
      var item = root.querySelector('[data-section-id="' + sectionId + '"]');
      var itemTrigger = item && item.querySelector('[data-section-trigger]');
      var itemPanel = item && item.querySelector('[data-section-panel]');
      var isOpen = sectionId === id;

      if (itemTrigger) itemTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (itemPanel) itemPanel.hidden = !isOpen;
    });

    revealedSections[id] = true;

    trigger = section.querySelector('[data-section-trigger]');
    if (options && options.focus && trigger && typeof trigger.focus === 'function') trigger.focus();
    if (options && (options.scroll || options.scrollContainer)) {
      scrollBehavior = 'smooth';
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) scrollBehavior = 'auto';

      if (options.scrollContainer) {
        scrollContainer = root.querySelector('[data-listing-form]');
        sectionIndex = sectionOrder.indexOf(id);
        if (scrollContainer && sectionIndex !== -1 && typeof scrollContainer.scrollTo === 'function') {
          window.setTimeout(function () {
            scrollContainer.scrollTo({ top: sectionIndex * 56, behavior: scrollBehavior });
          }, 200);
        }
      } else if (typeof section.scrollIntoView === 'function' && options.scrollAfterExpand) {
        window.setTimeout(function () {
          section.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
        }, 200);
      } else if (typeof section.scrollIntoView === 'function') {
        section.scrollIntoView({ behavior: scrollBehavior, block: 'start' });
      }
    }
    trackSectionOpened(id);
  }

  function trackSectionOpened(id) {
    var eventName = sectionEvents[id];
    if (!eventName || openedSections[id]) return;
    openedSections[id] = true;
    captureEvent('section_opened', { section: eventName });
  }

  function trackSectionCompletion(state) {
    sectionOrder.forEach(function (id) {
      var eventName = sectionEvents[id];
      if (!eventName || completedSections[id] || !validateSection(id, state).isValid) return;
      completedSections[id] = true;
      captureEvent('section_completed', { section: eventName });
    });

    funnelOrder.forEach(function (id, index) {
      var previousComplete = funnelOrder.slice(0, index).every(function (previousId) {
        return completedSections[previousId];
      });
      var eventName = sectionEvents[id];
      if (!eventName || funnelSteps[id] || !completedSections[id] || !previousComplete) return;
      funnelSteps[id] = true;
      captureEvent('funnel_step_completed', { section: eventName, step: index + 1 });
    });
  }

  function trackBikeValuationFieldCompletion(state) {
    if ((state || {}).internal_assisted_identification === true && !bikeValuationFieldsCompleted.assisted_identification_started) {
      bikeValuationFieldsCompleted.assisted_identification_started = true;
      captureEvent('bike_valuation_field_completed', {
        field: 'assisted_identification_started',
        step: 0,
        entry_point: (state || {}).internal_assisted_identification_entry_point || ''
      });
    }
    var order = (state || {}).internal_assisted_identification === true
      ? assistedBikeValuationFieldOrder : bikeValuationFieldOrder;
    order.forEach(function (key, index) {
      var value = (state || {})[key];
      if (bikeValuationFieldsCompleted[key]) return;
      if (value === '__other__' || value === '__model_other__') return;
      if (value === null || value === undefined || String(value).trim() === '') return;
      bikeValuationFieldsCompleted[key] = true;
      captureEvent('bike_valuation_field_completed', { field: key, step: index + 1 });
    });
  }

  function trackContactDetailsFieldCompletion(state) {
    contactDetailsFieldOrder.forEach(function (key, index) {
      var value = (state || {})[key];
      if (contactDetailsFieldsCompleted[key]) return;
      if (value === null || value === undefined || String(value).trim() === '') return;
      contactDetailsFieldsCompleted[key] = true;
      captureEvent('contact_details_field_completed', { field: key, step: index + 1 });
    });
  }

  function bindAccordion(root, store) {
    if (!root) return;
    root.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-section-trigger]');
      var section;
      if (!trigger || !root.contains(trigger)) return;
      section = trigger.closest('[data-section-id]');
      if (section) {
        openSection(section.getAttribute('data-section-id'), { root: root, scrollContainer: true });
        renderValidation(root, store.get(), section.getAttribute('data-section-id'));
      }
    });
    openSection(initialSectionId(store.get()), { root: root });
  }

  // FormAccordion.tsx:130-145 (getInitialOpenSection): resume on the first
  // section that still fails validation, or the last section when all pass.
  function initialSectionId(state) {
    var validation = validateForm(state || {});
    var firstIncomplete = sectionOrder.find(function (id) {
      return !(validation.sections && validation.sections[id] && validation.sections[id].isValid);
    });
    return firstIncomplete || sectionOrder[sectionOrder.length - 1];
  }

  function bindControls(root, store) {
    if (!root) return;
    var syncingSpecs = false;
    var syncingUpgrades = false;
    Array.prototype.slice.call(root.querySelectorAll('[data-field]')).forEach(function (control) {
      writeControl(control, store.get()[control.getAttribute('data-field')]);
    });

    function isValuePresent(value) {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'boolean') return value === true;
      return value !== null && value !== undefined && String(value).trim() !== '';
    }

    // Answering a field further down a section means the seller looked past
    // any earlier required field that's still blank — flag those now rather
    // than waiting for a blur that will never come if they never focus it.
    function touchSkippedPriorFields(control, value) {
      var sectionEl = control.closest('[data-section-id]');
      var key = control.getAttribute('data-field');
      var fields;
      var index;
      var state;
      var validation;
      if (!sectionEl || !isValuePresent(value)) return;
      fields = Array.prototype.slice.call(sectionEl.querySelectorAll('[data-field]'));
      index = fields.indexOf(control);
      if (index <= 0) return;
      state = store.get();
      validation = validateForm(state);
      fields.slice(0, index).forEach(function (priorControl) {
        var priorKey = priorControl.getAttribute('data-field');
        // Radio/checkbox groups repeat the same data-field on every option, so
        // an earlier option for this very field must not count as "prior".
        if (priorKey === key) return;
        // Composite spec fields (e.g. motor brand + model) attach their
        // "incomplete" error to the brand key even once the brand itself has
        // an answer, waiting on its paired model field — that's not a field
        // the seller skipped, so only flag keys that are genuinely still blank.
        if (validation.errors[priorKey] && !isValuePresent(state[priorKey])) touchedFields[priorKey] = true;
      });
    }

    function handleControlEvent(event) {
      var control = event.target.closest('[data-field]');
      var key;
      var value;
      if (!control || !root.contains(control)) return;
      key = control.getAttribute('data-field');
      value = readControl(control, store.get());
      if (key === 'internal_upgraded_components' && control.type === 'checkbox' && !control.checked) {
        clearUpgradeComponentFields(store, control.value);
      }
      touchSkippedPriorFields(control, value);
      store.set(key, value);
    }

    root.addEventListener('input', handleControlEvent);
    root.addEventListener('change', handleControlEvent);

    // The source app renders no field-level error text at all, only a section
    // badge. This port adds inline messages for accessibility, but they must
    // wait for the seller to actually leave a field empty, not fire the
    // instant its section opens — so touch is tracked per field, on blur.
    root.addEventListener('focusout', function (event) {
      var control = event.target && event.target.closest ? event.target.closest('[data-field]') : null;
      var key;
      if (!control || !root.contains(control)) return;
      key = control.getAttribute('data-field');
      if (touchedFields[key]) return;
      touchedFields[key] = true;
      renderValidation(root, store.get(), currentSectionId(root));
    });

    store.subscribe(function (state, changed) {
      var specField = specFieldForAnswerKey(changed);
      var composed;
      var changedParts;
      if (isUpgradeKey(changed) && !syncingUpgrades) {
        syncingUpgrades = true;
        changedParts = state.UpgradesReplacements === 'Yes' ? composeChangedParts(state) : '';
        if (!Object.is(state.ChangedPartsDescription, changedParts)) store.set('ChangedPartsDescription', changedParts);
        syncingUpgrades = false;
      }
      if (specField && !syncingSpecs) {
        syncingSpecs = true;
        composed = composeSpecFieldAnswer(specField, state);
        Object.keys(composed).forEach(function (key) {
          if (!Object.is(state[key], composed[key])) store.set(key, composed[key]);
        });
        syncingSpecs = false;
      }
      Array.prototype.slice.call(root.querySelectorAll('[data-field]')).filter(function (control) {
        return control.getAttribute('data-field') === changed;
      }).forEach(function (control) {
        writeControl(control, state[changed]);
      });
      renderValidation(root, state, currentSectionId(root));
      renderSubmitState(root, state);
    });
  }

  // ContactInformationSection.tsx upsell/suggestion clicks. Both are delegated
  // on the root: [data-sponsor-boost] sets sponsoredListing (the store
  // subscription re-renders and hides the panel), and
  // [data-email-suggestion-apply] rewrites user_Email to
  // <localPart>@<suggestedDomain> exactly like applySuggestedEmailDomain.
  function bindContactExtras(root, store) {
    if (!root || !store) return;
    root.addEventListener('click', function (event) {
      var boost = event.target.closest('[data-sponsor-boost]');
      var apply = event.target.closest('[data-email-suggestion-apply]');
      var email;
      var localPart;
      if (boost && root.contains(boost)) {
        store.set('sponsoredListing', true);
        return;
      }
      if (apply && root.contains(apply)) {
        email = String(store.get().user_Email || '');
        localPart = email.split('@')[0];
        if (localPart) store.set('user_Email', localPart + '@' + apply.textContent.trim());
      }
    });
  }

  // UpgradeComponentSelector.tsx:81-92 — the less-common upgrades grid sits
  // behind a "Show less common upgrades" toggle. The group's visibility is
  // independent of the store (like React's showSecondary state), so the
  // toggle only flips hidden/aria-expanded/label on the DOM pair.
  function bindUpgradeSecondaryToggle(root) {
    var toggle;
    var group;
    if (!root) return;
    toggle = root.querySelector('[data-upgrade-secondary-toggle]');
    group = root.querySelector('[data-upgrade-secondary]');
    if (!toggle || !group || toggle.getAttribute('data-upgrade-secondary-bound') === 'true') return;
    toggle.setAttribute('data-upgrade-secondary-bound', 'true');
    toggle.addEventListener('click', function () {
      var expanded = group.hidden;
      group.hidden = !expanded;
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      toggle.textContent = expanded ? 'Hide less common upgrades' : 'Show less common upgrades';
    });
  }

  function submissionDialog(root) {
    return root && root.querySelector('[data-submission-dialog]');
  }

  function openSubmissionDialog(root) {
    var dialog = submissionDialog(root);
    var heading = dialog && dialog.querySelector('[data-submission-title], h1, h2, h3');
    if (!dialog) return;
    if (typeof dialog.showModal === 'function' && !dialog.open) {
      try {
        dialog.showModal();
      } catch (_error) {
        dialog.setAttribute('open', '');
      }
    } else {
      dialog.setAttribute('open', '');
    }
    if (heading && typeof heading.focus === 'function') heading.focus();
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) {
      try {
        dialog.close();
        return;
      } catch (_error) {}
    }
    dialog.removeAttribute('open');
  }

  // PageSwitcher.tsx:52 — the submit button is disabled until every section
  // passes validation (isSubmitDisabled = !areAllSectionsCompleted). While a
  // submit is pending it stays disabled via setSubmitState's pending path, so
  // this helper never re-enables it mid-flight. Screen readers get an explicit
  // blocked state so the disabled reason is announced, not just implied.
  function renderSubmitState(root, state) {
    var form = root && root.querySelector('[data-listing-form]');
    var button = form && form.querySelector('[data-submit-button], button[type="submit"], input[type="submit"]');
    var isValid;

    if (!button) return;
    isValid = validateForm(state || {}).isValid;

    if (!isValid) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.title = 'Complete all sections to submit';
    } else if (!submitting) {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.removeAttribute('title');
    }
  }

  function setSubmitState(root, state, message) {
    var form = root && root.querySelector('[data-listing-form]');
    var button = form && form.querySelector('[data-submit-button], button[type="submit"], input[type="submit"]');
    var live = root && root.querySelector('[data-submission-live]');
    var dialogStatus = root && root.querySelector('[data-submission-status]');

    if (button) {
      if (state === 'pending') {
        if (!button.dataset.submitWidth && button.offsetWidth) {
          button.dataset.submitWidth = String(button.offsetWidth);
          button.style.minWidth = button.offsetWidth + 'px';
        }
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      } else if (state === 'succeeded') {
        // The listing already exists server-side; re-enabling here would let
        // a click during redirect (or on the fallback dialog) submit it again.
        button.disabled = true;
        button.removeAttribute('aria-busy');
      } else {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    if (state === 'pending') {
      startSubmitFeedback(root);
    } else {
      stopSubmitFeedback(root, state);
    }

    if (state === 'idle') return;

    if (live) {
      live.textContent = message || '';
      live.setAttribute('aria-live', state === 'failed' ? 'assertive' : 'polite');
      live.setAttribute('role', state === 'failed' ? 'alert' : 'status');
    }

    if (dialogStatus && state === 'succeeded') {
      dialogStatus.textContent = message || '';
      dialogStatus.setAttribute('aria-live', 'polite');
      dialogStatus.setAttribute('role', 'status');
    }
  }

  // Pending-submit feedback: an ink-600 fill sweeping the button behind the
  // label plus a mono record stamping elapsed seconds above it. The fill runs
  // an exponential tail easing toward SUBMIT_PROGRESS_CAP — movement is visible
  // within the first second, but 100% is never reached while the request is in
  // flight, so the UI promises nothing the request hasn't delivered. Timing is
  // measured against the clock (not frame counts), so a backgrounded tab
  // catches up instead of freezing; the record's seconds keep ticking either
  // way, which is what reads as "working, not stuck" on long requests.
  var SUBMIT_PROGRESS_CAP = 0.9;
  var SUBMIT_PROGRESS_TAIL_MS = 9000;
  var submitFeedback = null;

  function submitButton(root) {
    var form = root && root.querySelector('[data-listing-form]');
    return form && form.querySelector('[data-submit-button], button[type="submit"], input[type="submit"]');
  }

  function submitLabelOf(button) {
    return button && (button.querySelector('[data-submit-label]') || button);
  }

  function formatSubmitElapsed(totalSeconds) {
    var minutes = Math.floor(totalSeconds / 60);
    var seconds = totalSeconds % 60;
    return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
  }

  function paintSubmitProgress() {
    var elapsed = Date.now() - submitFeedback.start;
    var progress = SUBMIT_PROGRESS_CAP * (1 - Math.exp(-elapsed / SUBMIT_PROGRESS_TAIL_MS));
    var seconds = Math.floor(elapsed / 1000);
    var record;

    if (submitFeedback.button) {
      submitFeedback.button.style.setProperty('--nl-progress', progress.toFixed(4));
    }
    if (seconds !== submitFeedback.lastSecond) {
      submitFeedback.lastSecond = seconds;
      record = submitFeedback.root.querySelector('[data-submit-record]');
      if (record) record.textContent = 'Submission · ' + formatSubmitElapsed(seconds);
    }
  }

  function startSubmitFeedback(root) {
    var button = submitButton(root);
    var label = submitLabelOf(button);
    var record = root && root.querySelector('[data-submit-record]');
    var reducedMotion = Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var self;

    stopSubmitFeedback(root, 'reset');

    if (label) {
      if (!label.dataset.submitLabelRest) label.dataset.submitLabelRest = label.textContent;
      label.textContent = 'Submitting…';
    }
    if (record) {
      record.removeAttribute('hidden');
      record.removeAttribute('data-submit-record-state');
      record.textContent = 'Submission · 0:00';
    }
    if (!button) return;

    self = { root: root, button: button, start: Date.now(), lastSecond: -1, frameId: 0, timerId: 0 };
    submitFeedback = self;
    paintSubmitProgress();
    if (reducedMotion) {
      // No continuous sweep under reduced motion: the fill steps once per
      // second alongside the record, which carries the liveness signal.
      self.timerId = window.setInterval(function () {
        if (submitFeedback === self) paintSubmitProgress();
      }, 1000);
    } else if (typeof window.requestAnimationFrame === 'function') {
      var frame = function () {
        if (submitFeedback !== self) return;
        paintSubmitProgress();
        self.frameId = window.requestAnimationFrame(frame);
      };
      self.frameId = window.requestAnimationFrame(frame);
    } else {
      // jsdom and other engines without rAF still get paint updates.
      self.timerId = window.setInterval(function () {
        if (submitFeedback === self) paintSubmitProgress();
      }, 100);
    }
  }

  function stopSubmitFeedback(root, state) {
    var feedback = submitFeedback;
    var button = submitButton(root);
    var label = submitLabelOf(button);
    var record = root && root.querySelector('[data-submit-record]');

    if (feedback) {
      if (feedback.frameId && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(feedback.frameId);
      if (feedback.timerId) window.clearInterval(feedback.timerId);
      submitFeedback = null;
    }
    if (button) button.style.removeProperty('--nl-progress');
    if (label && label.dataset.submitLabelRest) label.textContent = label.dataset.submitLabelRest;
    if (record) {
      if (state === 'failed') {
        // Failure keeps the record visible so the outcome isn't announced to
        // screen readers only; the next pending submit rewrites it.
        record.textContent = 'Submission failed · Try again';
        record.setAttribute('data-submit-record-state', 'failed');
      } else {
        record.setAttribute('hidden', '');
      }
    }
  }

  function parseSubmissionResponse(response) {
    var contentType = response && response.headers && typeof response.headers.get === 'function' ? response.headers.get('content-type') : '';
    if (!contentType || contentType.indexOf('application/json') === -1 || typeof response.json !== 'function') {
      return Promise.resolve(null);
    }
    return response.json().catch(function (parseError) {
      if (window.console && typeof window.console.warn === 'function') window.console.warn('Failed to parse JSON response:', parseError);
      return null;
    });
  }

  // The Google Ads sign-up conversion. listing-submission already sends the GA4
  // conversion server-side; this is the browser half Google Ads needs at the
  // moment the submission is confirmed, before either exit below moves the
  // seller anywhere. Consent Mode (ad_user_data, driven by the Consentmo
  // banner) decides whether the identifiers may be used, so nothing here is
  // gated on consent.
  var ADS_CONVERSION_ID = 'AW-11093686858';
  var ADS_CONVERSION_SEND_TO = 'AW-11093686858/k_6pCKPB0oEdEMr08Kkp';
  var ADS_CONVERSION_TIMEOUT = 1000;

  // Enhanced conversions match on the values the edge function normalises
  // before hashing them for Google
  // (mnb-edge/supabase/functions/_shared/ads/google.ts): address lowercased
  // with Gmail's dots dropped, phone in E.164. The phone field stores the UK
  // national 07… form, which Google can never match as it stands.
  function normalizeAdsEmail(value) {
    var cleaned = normalizeEmail(value);
    var at = cleaned.lastIndexOf('@');
    if (at === -1) return cleaned;
    var local = cleaned.slice(0, at);
    var domain = cleaned.slice(at + 1);
    if (domain === 'gmail.com' || domain === 'googlemail.com') local = local.replace(/\./g, '');
    return local + '@' + domain;
  }

  function normalizeAdsPhone(value) {
    var digits = String(value || '').replace(/[^0-9]/g, '');
    if (!digits) return '';
    if (digits.indexOf('44') === 0) return '+' + digits;
    if (digits.indexOf('0') === 0) return '+44' + digits.slice(1);
    return '+' + digits;
  }

  // Fires the Ads conversion and reports back through `done` exactly once —
  // either when Google confirms the event was processed (event_callback) or
  // after a hard timeout, so a tag that never answers cannot strand the
  // sponsored redirect. Without a callback the submission path is unaffected.
  function sendAdsSignup(listingId, email, phone, done) {
    var sent = false;
    var userData = {};

    function next() {
      if (sent) return;
      sent = true;
      if (typeof done === 'function') done();
    }

    try {
      if (typeof window.gtag !== 'function') throw new Error('gtag unavailable');
      if (email) userData.email = email;
      if (phone) userData.phone_number = phone;
      if (userData.email || userData.phone_number) window.gtag('set', 'user_data', userData);
      window.gtag('config', ADS_CONVERSION_ID, { send_page_view: false });
      window.gtag('event', 'conversion', {
        send_to: ADS_CONVERSION_SEND_TO,
        transaction_id: String(listingId),
        event_callback: next
      });
    } catch (error) {
      next();
    }

    setTimeout(next, ADS_CONVERSION_TIMEOUT);
  }

  async function handleSubmissionSuccess(root, state, response) {
    var data = await parseSubmissionResponse(response);
    if (!data || !data.listingId) {
      throw new Error('Listing submission response missing listingId');
    }
    var redirectHref = data && data.redirectUrl ? resolveSafeRedirect(data.redirectUrl) : '';
    var properties = submissionProperties(state);
    if (data && data.listingId) properties.listing_id = data.listingId;
    // listing-submission fires the GA4 conversion server-side; this id is kept
    // only so the thank-you page can tell a fresh submission from a revisit.
    if (data && data.eventId && window.sessionStorage) {
      window.sessionStorage.setItem('listing_event_id', data.eventId);
    }
    var adsEmail = normalizeAdsEmail(state.user_Email);
    var adsPhone = normalizeAdsPhone(state.user_PhoneNumber);
    clearDraft(window.localStorage);
    captureEvent('form_submitted', properties);
    if (redirectHref) {
      // Sponsored listings leave for the Shopify checkout straight away, so the
      // Ads conversion has to be sent before that navigation can cut it off.
      sendAdsSignup(data.listingId, adsEmail, adsPhone, function () {
        safeRedirect(redirectHref);
      });
      return;
    }
    sendAdsSignup(data.listingId, adsEmail, adsPhone);
    setSubmitState(root, 'succeeded', 'Your listing has been submitted successfully.');
    openSubmissionDialog(root);
  }

  function handleSubmissionFailure(root, error) {
    if (window.console && typeof window.console.error === 'function') window.console.error('Submission error:', error);
    setSubmitState(root, 'failed', 'Submission failed. Please try again.');
    captureEvent('form_submit_failed', { error: error && error.message ? error.message : String(error) });
  }

  function bindSubmit(root, store, config) {
    var form = root && root.querySelector('[data-listing-form]');
    if (!form) return;
    root.addEventListener('click', function (event) {
      // Every <dialog> on this page (handoff, manual-entry, submission) shares
      // the [data-dialog-close] convention, so its close button must close
      // whichever dialog it is inside, not only the submission one.
      var close = event.target.closest('[data-dialog-close]');
      var dialog = close && close.closest('dialog');
      var proceed = event.target.closest('[data-submission-continue]');
      if (proceed && root.contains(proceed)) {
        safeRedirect(THANK_YOU_URL);
        return;
      }
      if (dialog && root.contains(dialog)) closeDialog(dialog);
    });
    form.addEventListener('submit', async function (event) {
      var validation;
      var state;
      var response;
      var integrations;
      var email;
      event.preventDefault();
      if (submitting) return;
      validation = renderValidation(root, store.get(), currentSectionId(root), { summary: true });
      state = store.get();
      integrations = integrationsAdapter();

      if (!validation.isValid) {
        // A submit attempt is the other case (besides blur) that should
        // surface inline messages, matching the error summary already
        // listing every invalid field regardless of what the seller opened.
        Object.keys(validation.errors).forEach(function (field) {
          touchedFields[field] = true;
        });
        focusFirstInvalid(root, validation);
        // focusFirstInvalid opens the first invalid section, so re-render to
        // surface that section's newly touched inline errors.
        renderValidation(root, store.get(), currentSectionId(root), { summary: true });
        renderSubmitState(root, store.get());
        return;
      }

      email = normalizeEmail(state.user_Email);
      if (email && email.indexOf('@') > -1 && integrations && typeof integrations.identify === 'function') {
        integrations.identify(email);
      }

      submitting = true;
      setSubmitState(root, 'pending', 'Submitting your listing…');
      captureEvent('form_submit_clicked', {});
      try {
        response = await fetch((config || {}).submissionUrl, {
          method: 'POST',
          body: JSON.stringify(prepareSubmissionPayload(Object.assign({}, state, collectListingTrackingData()))),
          headers: { 'Content-Type': 'application/json' },
          redirect: 'manual'
        });
        if (!response || response.status < 200 || response.status >= 300) {
          throw new Error('Submission failed with status ' + (response ? response.status : 0));
        }
        await handleSubmissionSuccess(root, state, response);
      } catch (error) {
        submitting = false;
        handleSubmissionFailure(root, error);
      }
    });
  }

  // On mobile the sticky submit bar sits over the form scroller. Publish its
  // measured height so new-listing.css can reserve exactly that much room
  // instead of a hard-coded constant. The preview trigger lives in the header
  // now, so it no longer stacks with the submit bar and needs no measurement.
  function measureBottomInset(root) {
    var bar;
    var barHeight;

    if (!root || !root.style) return;

    if (window.matchMedia && window.matchMedia('(min-width: 1024px)').matches) {
      root.style.removeProperty('--nl-submit-bar-height');
      return;
    }

    bar = root.querySelector('.nl-submit-bar');
    barHeight = bar && bar.getBoundingClientRect ? bar.getBoundingClientRect().height : 0;

    // jsdom and pre-layout paints report 0; keep the stylesheet fallback then.
    if (barHeight > 0) root.style.setProperty('--nl-submit-bar-height', barHeight + 'px');
  }

  function bindBottomInset(root) {
    var observer;
    var bar;
    var remeasure = function () {
      measureBottomInset(root);
    };

    if (!root) return;
    measureBottomInset(root);
    window.addEventListener('resize', remeasure);
    window.addEventListener('orientationchange', remeasure);

    bar = root.querySelector('.nl-submit-bar');
    if (bar && typeof window.ResizeObserver === 'function') {
      observer = new window.ResizeObserver(remeasure);
      observer.observe(bar);
    }
  }

  // The listing is a viewport-sized app shell whose form owns scrolling.
  // Lock the document itself so iOS cannot pan the layout viewport to focused
  // inputs and leave that outer scroll offset behind when the keyboard closes.
  function lockOuterPageScroll(root) {
    if (!root || !document.documentElement) return;
    document.documentElement.classList.add('nl-page-scroll-lock');
  }

  function boot(rootArg) {
    var root = rootArg || document.querySelector('[data-new-listing-root]');
    var initial = loadDraft(window.localStorage);
    var hydration = parseHydration(window.location.search);
    var config = readConfig();
    var store;

    revealedSections = Object.create(null);
    touchedFields = Object.create(null);
    bikeValuationFieldsCompleted = Object.create(null);
    contactDetailsFieldsCompleted = Object.create(null);

    lockOuterPageScroll(root);

    if (hydration) {
      Object.assign(initial, hydrationToForm(hydration));
      removeHydrationParams();
    }

    store = createStore(initial);
    store.subscribe(function (state) {
      persistDraft(window.localStorage, state);
    });
    bindAccordion(root, store);
    bindControls(root, store);
    bindContactExtras(root, store);
    bindUpgradeSecondaryToggle(root);
    bindSubmit(root, store, config);
    bindPreview(root, store);
    bindBottomInset(root);
    if (root) {
      renderValidation(root, store.get(), currentSectionId(root));
      renderSubmitState(root, store.get());
    }
    initializeModules(root, store, config);
    if (root) trackSectionOpened(currentSectionId(root));
    trackSectionCompletion(store.get());
    trackBikeValuationFieldCompletion(store.get());
    trackContactDetailsFieldCompletion(store.get());
    store.subscribe(function (state) {
      trackSectionCompletion(state);
      trackBikeValuationFieldCompletion(state);
      trackContactDetailsFieldCompletion(state);
    });
    bootedStore = store;
    bootedRoot = root;
    bootedConfig = config;
    return store;
  }

  window.MNBListing = {
    FORM_KEY: FORM_KEY,
    MAX_PHOTOS: MAX_PHOTOS,
    MODELS_KEY: MODELS_KEY,
    blankForm: blankForm,
    register: register,
    module: function (name) {
      return modules[name];
    },
    createStore: createStore,
    clearDraft: clearDraft,
    parseHydration: parseHydration,
    processFormData: processFormData,
    prepareSubmissionPayload: prepareSubmissionPayload,
    safeRedirect: safeRedirect,
    sendAdsSignup: sendAdsSignup,
    normalizeAdsEmail: normalizeAdsEmail,
    normalizeAdsPhone: normalizeAdsPhone,
    applyCommission: applyCommission,
    getCommissionPercent: getCommissionPercent,
    formatGBP: formatGBP,
    validators: validators,
    validationRuleGroups: validationRuleGroups,
    validateSection: validateSection,
    validateForm: validateForm,
    resolveBikeSpecCategory: resolveBikeSpecCategory,
    requiredSpecFields: requiredSpecFields,
    composeSpecAnswers: composeSpecAnswers,
    openSection: openSection,
    measureBottomInset: measureBottomInset,
    initialSectionId: initialSectionId,
    composeChangedParts: composeChangedParts,
    upgradeComponents: upgradeComponents,
    secondaryUpgradeComponents: secondaryUpgradeComponents,
    allUpgradeComponents: allUpgradeComponents,
    THANK_YOU_URL: THANK_YOU_URL,
    boot: boot
  };

  suppressKlaviyoFlyout();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      if (!bootedStore && document.querySelector('[data-new-listing-root]')) boot();
    });
  } else if (document.querySelector('[data-new-listing-root]')) {
    boot();
  }
})(window, document);
